/**
 * Field catalog for the HR Analytics pivot report builder.
 *
 * Single source of truth describing every field available for grouping
 * (dimensions) and aggregation (measures). The `key` of each field MUST match a
 * column key on `FlatEmployeeRow` returned by `GET /api/analytics/pivot-dataset`.
 *
 * This module is pure data + helpers (no React) so it can be reused by the
 * desktop and mobile pivot builders and the pivot engine.
 */

export type FieldType = "dimension" | "measure";
export type AggFn = "count" | "sum" | "avg" | "min" | "max";
export type FieldGroup =
  | "Identity"
  | "Org"
  | "Personal"
  | "Employment"
  | "Salary"
  | "Leave"
  | "Compliance"
  | "Counts";

export interface FieldDef {
  /** Column key on FlatEmployeeRow — must match the backend SELECT alias 1:1. */
  key: keyof FlatEmployeeRow;
  /** i18n key for the human label (analytics.pivot.fields.*). */
  labelKey: string;
  group: FieldGroup;
  type: FieldType;
  /** Allowed aggregations — measures only. */
  allowedAggs?: AggFn[];
}

/**
 * One row per employee. Keys MUST match the SELECT aliases of the
 * `/api/analytics/pivot-dataset` endpoint exactly.
 */
export interface FlatEmployeeRow {
  // Identity
  id: number;
  name: string;
  employee_id: string | null;
  email: string;
  // Org (dimensions)
  department: string | null;
  team_name: string | null;
  location: string | null;
  company: string | null;
  manager: string | null;
  position_title: string | null;
  cost_center: string | null;
  career_level: string | null;
  work_arrangement: string | null;
  // Employment (dimensions)
  status: string | null;
  contract_type: string | null;
  work_schedule: string | null;
  is_line_manager: boolean | null;
  benefits_eligible: boolean | null;
  overtime_eligible: boolean | null;
  start_date: string | null;
  start_year: number | null;
  contract_end_date: string | null;
  // Personal (dimensions)
  gender: string | null;
  nationality: string | null;
  // Compliance (dimensions)
  medical_status: string | null;
  visa_permit_type: string | null;
  // Measures (numeric)
  base_salary: number | null;
  bonus: number | null;
  currency: string | null;
  fte_percentage: number | null;
  hours_per_week: number | null;
  tenure_months: number | null;
  leave_total: number | null;
  leave_used: number | null;
  leave_remaining: number | null;
  qualifications_count: number;
  certifications_count: number;
  equipment_count: number;
  children_count: number;
  direct_reports_count: number;
}

const NUM_AGGS: AggFn[] = ["sum", "avg", "min", "max", "count"];

export const FIELD_CATALOG: FieldDef[] = [
  // ── Identity ──────────────────────────────────────────────
  { key: "name", labelKey: "analytics.pivot.fields.name", group: "Identity", type: "dimension" },
  { key: "employee_id", labelKey: "analytics.pivot.fields.employee_id", group: "Identity", type: "dimension" },
  { key: "email", labelKey: "analytics.pivot.fields.email", group: "Identity", type: "dimension" },

  // ── Org ───────────────────────────────────────────────────
  { key: "department", labelKey: "analytics.pivot.fields.department", group: "Org", type: "dimension" },
  { key: "team_name", labelKey: "analytics.pivot.fields.team_name", group: "Org", type: "dimension" },
  { key: "location", labelKey: "analytics.pivot.fields.location", group: "Org", type: "dimension" },
  { key: "company", labelKey: "analytics.pivot.fields.company", group: "Org", type: "dimension" },
  { key: "manager", labelKey: "analytics.pivot.fields.manager", group: "Org", type: "dimension" },
  { key: "position_title", labelKey: "analytics.pivot.fields.position_title", group: "Org", type: "dimension" },
  { key: "cost_center", labelKey: "analytics.pivot.fields.cost_center", group: "Org", type: "dimension" },
  { key: "career_level", labelKey: "analytics.pivot.fields.career_level", group: "Org", type: "dimension" },
  { key: "work_arrangement", labelKey: "analytics.pivot.fields.work_arrangement", group: "Org", type: "dimension" },

  // ── Employment ────────────────────────────────────────────
  { key: "status", labelKey: "analytics.pivot.fields.status", group: "Employment", type: "dimension" },
  { key: "contract_type", labelKey: "analytics.pivot.fields.contract_type", group: "Employment", type: "dimension" },
  { key: "work_schedule", labelKey: "analytics.pivot.fields.work_schedule", group: "Employment", type: "dimension" },
  { key: "is_line_manager", labelKey: "analytics.pivot.fields.is_line_manager", group: "Employment", type: "dimension" },
  { key: "benefits_eligible", labelKey: "analytics.pivot.fields.benefits_eligible", group: "Employment", type: "dimension" },
  { key: "overtime_eligible", labelKey: "analytics.pivot.fields.overtime_eligible", group: "Employment", type: "dimension" },
  { key: "start_year", labelKey: "analytics.pivot.fields.start_year", group: "Employment", type: "dimension" },
  { key: "start_date", labelKey: "analytics.pivot.fields.start_date", group: "Employment", type: "dimension" },
  { key: "contract_end_date", labelKey: "analytics.pivot.fields.contract_end_date", group: "Employment", type: "dimension" },

  // ── Personal ──────────────────────────────────────────────
  { key: "gender", labelKey: "analytics.pivot.fields.gender", group: "Personal", type: "dimension" },
  { key: "nationality", labelKey: "analytics.pivot.fields.nationality", group: "Personal", type: "dimension" },

  // ── Compliance ────────────────────────────────────────────
  { key: "medical_status", labelKey: "analytics.pivot.fields.medical_status", group: "Compliance", type: "dimension" },
  { key: "visa_permit_type", labelKey: "analytics.pivot.fields.visa_permit_type", group: "Compliance", type: "dimension" },

  // ── Salary (measures) ─────────────────────────────────────
  { key: "base_salary", labelKey: "analytics.pivot.fields.base_salary", group: "Salary", type: "measure", allowedAggs: NUM_AGGS },
  { key: "bonus", labelKey: "analytics.pivot.fields.bonus", group: "Salary", type: "measure", allowedAggs: NUM_AGGS },

  // ── Employment measures ───────────────────────────────────
  { key: "fte_percentage", labelKey: "analytics.pivot.fields.fte_percentage", group: "Employment", type: "measure", allowedAggs: NUM_AGGS },
  { key: "hours_per_week", labelKey: "analytics.pivot.fields.hours_per_week", group: "Employment", type: "measure", allowedAggs: NUM_AGGS },
  { key: "tenure_months", labelKey: "analytics.pivot.fields.tenure_months", group: "Employment", type: "measure", allowedAggs: NUM_AGGS },

  // ── Leave (measures) ──────────────────────────────────────
  { key: "leave_total", labelKey: "analytics.pivot.fields.leave_total", group: "Leave", type: "measure", allowedAggs: NUM_AGGS },
  { key: "leave_used", labelKey: "analytics.pivot.fields.leave_used", group: "Leave", type: "measure", allowedAggs: NUM_AGGS },
  { key: "leave_remaining", labelKey: "analytics.pivot.fields.leave_remaining", group: "Leave", type: "measure", allowedAggs: NUM_AGGS },

  // ── Counts (measures) ─────────────────────────────────────
  { key: "qualifications_count", labelKey: "analytics.pivot.fields.qualifications_count", group: "Counts", type: "measure", allowedAggs: NUM_AGGS },
  { key: "certifications_count", labelKey: "analytics.pivot.fields.certifications_count", group: "Counts", type: "measure", allowedAggs: NUM_AGGS },
  { key: "equipment_count", labelKey: "analytics.pivot.fields.equipment_count", group: "Counts", type: "measure", allowedAggs: NUM_AGGS },
  { key: "children_count", labelKey: "analytics.pivot.fields.children_count", group: "Counts", type: "measure", allowedAggs: NUM_AGGS },
  { key: "direct_reports_count", labelKey: "analytics.pivot.fields.direct_reports_count", group: "Counts", type: "measure", allowedAggs: NUM_AGGS },
];

export const AGG_FNS: AggFn[] = ["count", "sum", "avg", "min", "max"];

export function getDimensionFields(): FieldDef[] {
  return FIELD_CATALOG.filter((f) => f.type === "dimension");
}

export function getMeasureFields(): FieldDef[] {
  return FIELD_CATALOG.filter((f) => f.type === "measure");
}

export function getFieldByKey(key: string): FieldDef | undefined {
  return FIELD_CATALOG.find((f) => f.key === key);
}

/** Fields offered as columns in flat (per-employee) mode — everything. */
export function getAllFields(): FieldDef[] {
  return FIELD_CATALOG;
}
